import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from "motion/react";
import { useSearchParams } from 'react-router-dom';
import { CartGroupCard } from './CartGroupCard';
import { TicketModal } from './TicketModal';
import type { GroupedCartResponse } from '@/features/booking/types/bookingTypes';

interface CartGroupedTicketsViewProps {
  groupedCarts: GroupedCartResponse[];
}

export const CartGroupedTicketsView: React.FC<CartGroupedTicketsViewProps> = ({ groupedCarts }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);

  // Check for bookingId in URL params on mount and when searchParams change
  useEffect(() => {
    const bookingIdFromUrl = searchParams.get('bookingId');
    if (bookingIdFromUrl) {
      setSelectedBookingId(bookingIdFromUrl);
    }
  }, [searchParams]);

  const handleCloseModal = () => {
    setSelectedBookingId(null);
    // Clear bookingId from URL when modal is closed
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('bookingId');
    setSearchParams(newParams);
  };

  const handleBookingClick = (bookingId: string) => {
    if (bookingId) {
      setSelectedBookingId(bookingId);
    }
  };

  // Sort by cart creation/expiry date (most recent first)
  const sortedCarts = [...groupedCarts].sort((a, b) =>
    new Date(b.cart.expires_at).getTime() - new Date(a.cart.expires_at).getTime()
  );

  if (sortedCarts.length === 0) {
    return (
      <motion.div
        className="text-center py-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.4 }}
      >
        <motion.p
          className="text-gray-400"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.4 }}
        >
          You don't have any tickets yet.
        </motion.p>
      </motion.div>
    );
  }

  return (
    <>
      <motion.div
        className="space-y-3 sm:space-y-4 lg:space-y-6 pb-2 sm:pb-3 lg:pb-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.4 }}
      >
        {sortedCarts.map((groupedCart, index) => (
          <motion.div
            key={groupedCart.cart.id}
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
              delay: 0.3 + index * 0.1,
              type: "spring",
              stiffness: 300,
              damping: 30,
              mass: 0.8
            }}
          >
            <CartGroupCard
              cart={groupedCart.cart}
              bookings={groupedCart.bookings}
              onBookingClick={handleBookingClick}
            />
          </motion.div>
        ))}
      </motion.div>

      {/* Ticket Modal */}
      <AnimatePresence mode="wait">
        {selectedBookingId && (
          <TicketModal key={selectedBookingId} bookingId={selectedBookingId} onClose={handleCloseModal} />
        )}
      </AnimatePresence>
    </>
  );
};

